import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import type { DateRange, Lodging } from "@b2bportal/lodging-api";
import { useWindowVirtualizer } from "@tanstack/react-virtual";
import {
  HotelListNoHotels,
  HotelListItem,
  useHotelsAvailability,
  HotelListItem_Loading,
} from "@tenant/hotels";
import clsx from "clsx";
import { useHotelsConfig } from "../../../../context/HotelsConfigContext";

import defaultStyles from "./HotelList.module.scss";
import { HotelsAvailabilitySelectors } from "~core-hotels/store";

export interface HotelListComponentProps extends PlatformComponentProps {
  hotelList?: Lodging[];
  onHover?: (hotelId: string | undefined) => void;
  className?: string;
  config: {
    initialResultSetSize: number;
    showMoreAmount: number;
  };
  rooms?: number;
  dateRange?: DateRange;
  isHotelListFiltering?: boolean;
  isLoading?: boolean;
}

export const HotelList = ({
  onHover,
}: {
  onHover?: (hotelId: string | undefined) => void;
}) => {
  const { context } = useHotelsAvailability();
  const { hotelsList } = context;
  const search = useSelector(HotelsAvailabilitySelectors.getRequestedSearch);
  const { availability } = useHotelsConfig();

  return (
    <HotelListComponent
      onHover={onHover}
      hotelList={hotelsList}
      config={availability}
      dateRange={search?.dateRange}
      rooms={context.rooms}
      isHotelListFiltering={context.isHotelListFiltering}
      isLoading={context.isLoading || context.isLoadingNext}
    />
  );
};

export const HotelListComponent = ({
  hotelList: propHotelList = [],
  onHover,
  className,
  children,
  config,
  dateRange,
  rooms,
  isHotelListFiltering = false,
  isLoading = false,
}: HotelListComponentProps) => {
  const styles = useHotelsStyles(CoreHotelsComponent.HotelList, defaultStyles);
  const [block, cn] = useModuleBEM(styles, CoreHotelsComponent.HotelList);

  const listRef = useRef(null);

  const hotelList = useMemo(() => {
    if (isLoading) {
      return [...propHotelList, { loading: true }];
    } else {
      return propHotelList;
    }
  }, [isLoading, propHotelList]);
  const virtualizer = useWindowVirtualizer({
    count: hotelList?.length ?? 0,
    estimateSize: () => 200,
    overscan: 5,
    // Fix for maximum depth reached: https://github.com/TanStack/virtual/issues/924
    measureElement: (element, _roEntry, instance) => {
      const cachedSize =
        instance.measurementsCache[instance.indexFromElement(element)].size;
      const rect = element.getBoundingClientRect();
      if (rect.height > 0 && rect.width > 0) {
        return Math.round(rect.height);
      }
      return cachedSize;
    },
  });

  return hotelList != null && hotelList.length > 0 && dateRange ? (
    <div ref={listRef} className={clsx(block, className)}>
      <ul
        className={cn("list")}
        style={{
          position: "relative",
          height: `${virtualizer.getTotalSize()}px`,
        }}
      >
        {virtualizer.getVirtualItems().map((virtualItem) => {
          const hotel = hotelList[virtualItem.index];

          return (
            <li
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={virtualizer.measureElement}
              className={cn("item")}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${virtualItem.start}px)`,
              }}
            >
              {"loading" in hotel ? (
                <HotelListItem_Loading />
              ) : (
                <HotelListItem
                  hotel={hotel}
                  onHover={onHover}
                  dateRange={dateRange}
                  rooms={rooms ?? 1}
                />
              )}
            </li>
          );
        })}

        {children}
      </ul>
    </div>
  ) : !isHotelListFiltering ? (
    <HotelListNoHotels className={cn("error")} />
  ) : null;
};
